/**
 * Advanced Text Rendering Engine
 * 
 * Extracted from ClassicCaptionPage.tsx to provide sophisticated text rendering
 * capabilities for both caption and text-overlay systems in timeloom.
 * 
 * Features:
 * - 14 text animation types with spring physics
 * - 6 outline effect types (glow, shadow, 3d-shadow, stroke, 3d-stroke)
 * - Professional color contrast calculation
 * - SVG-based multi-layer rendering
 * - Word-by-word animation timing
 * - Canvas-based font measurement for precision
 */

import React from 'react';
import { useCurrentFrame, spring, interpolate } from 'remotion';
import { 
    AdvancedTextEngineProps, 
    AdvancedTextConfig, 
    TextToken,
    TextAnimationType,
    OutlineType,
    BackgroundType,
    LayerType,
    OutlineRenderProps,
    CaptionCase
} from './types';

// Enhanced font system integration with clipCreator Google Fonts
import { INTEGRATED_FONT_MAP, resolveEnhancedFontFamily, initializeTimeloomFontSystem, FontLoader } from './FontUtils';

// Import enhanced color contrast validation system
import ColorContrastValidator, { 
    validateContrast, 
    getBestContrastColor, 
    quickContrastCheck,
    ContrastContext 
} from './ColorContrastValidator';

/**
 * Enhanced color utilities with improved accessibility
 * Now using the comprehensive ColorContrastValidator system
 */
const colorNameToHex = ColorContrastValidator.colorNameToHex;

// Use enhanced functions from ColorContrastValidator

const calculateContrastRatio = ColorContrastValidator.calculateContrastRatio;

/**
 * Enhanced contrast checking using the comprehensive validation system
 */
function checkContrast(
    fixedColor: string, 
    changeableColor: string, 
    context?: Partial<ContrastContext>
): {
    sufficientContrast: boolean,
    replacementColor: string,
    contrastRatio?: number,
    wcagLevel?: 'FAIL' | 'AA' | 'AAA',
    suggestion?: string
} {
    const result = validateContrast(changeableColor, fixedColor, context);
    
    return {
        sufficientContrast: result.sufficientContrast,
        replacementColor: result.replacementColor || getBestContrastColor(fixedColor),
        contrastRatio: result.contrastRatio,
        wcagLevel: result.wcagLevel,
        suggestion: result.suggestion
    };
}

// Use the original ClassicCaptionPage contrast logic that favors white text
const getContrastColor = (hexColor: string): string => {
    const color = colorNameToHex(hexColor);
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    // Calculate luminance using the formula for relative luminance in sRGB space
    // This follows the W3C accessibility guidelines
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Use a higher threshold to favor white text
    // Only use black text for very light colors (luminance > 0.7)
    return luminance > 0.7 ? 'black' : 'white';
};

/**
 * Background conversion utilities
 */
const backgroundToCSS = (background: BackgroundType, primaryColor: string): string => {
    switch (background) {
        case 'none': return 'transparent';
        case 'white': return 'rgba(255, 255, 255, 1)';
        case 'colored': return primaryColor;
        case 'translucent': return 'rgba(0, 0, 0, 0.7)';
    }
};

/**
 * Animation type checking utilities
 */
const isTextWithOutline = (outline: OutlineType): boolean => {
    return outline === '3d-shadow' || outline === 'stroke' || outline === '3d-stroke';
};

const isTextAnimationWithBox = (textAnimation: TextAnimationType): boolean => {
    return textAnimation === 'boxed' || textAnimation === 'karaoke_box' || textAnimation === 'reveal_box';
};

const isTextAnimationWithTextHighlightColor = (textAnimation: TextAnimationType): boolean => {
    return !isTextAnimationWithBox(textAnimation) && textAnimation !== 'none';
};

const isKaraokeAnimation = (textAnimation: TextAnimationType): boolean => {
    return textAnimation === 'karaoke' || textAnimation === 'karaoke_scale' || textAnimation === 'karaoke_box';
};

/**
 * Calculate the maximum scale factor for text animations
 * Used to ensure SVG containers are sized appropriately for scaled text
 */
const getMaxScaleFactor = (textAnimation: TextAnimationType): number => {
    // Scale animations that scale to 1.1 (110%)
    if (textAnimation === 'scale' || 
        textAnimation === 'reveal_scale' || 
        textAnimation === 'karaoke_scale') {
        return 1.1;
    }
    
    // Pop animation scales to 1.25 (125%) temporarily
    if (textAnimation === 'pop') {
        return 1.25;
    }
    
    // No scaling for other animations
    return 1.0;
};

/**
 * Enhanced professional color calculation with comprehensive contrast validation
 */
const getOutlineAndTextColorForStyle = (
    outline: OutlineType,
    textAnimation: TextAnimationType,
    active: boolean,
    background: BackgroundType,
    currentSpokenWordColor: string,
    primaryColor: string,
    hasBeenSpoken: boolean = false,
    fontSize: number = 60
): { outlineColor: string | undefined; textColor: string } => {
    const hasOutline = isTextWithOutline(outline);
    
    // Match original logic: when background is 'none', there's no background color to check against
    let backgroundColor = background === 'none' ? undefined : 
        (background === "translucent" ? 'black' : 
        (background === 'white' ? 'white' : primaryColor));
    let outlineColor = hasOutline ? primaryColor : undefined;

    // DEBUG LOGGING
    console.log('🎨 COLOR DEBUG START:', {
        textAnimation,
        active,
        hasBeenSpoken,
        background,
        primaryColor,
        currentSpokenWordColor,
        backgroundColor,
        outline,
        outlineColor: outlineColor,
        isKaraoke: isKaraokeAnimation(textAnimation),
        isHighlightAnimation: isTextAnimationWithTextHighlightColor(textAnimation)
    });

    if (outlineColor && backgroundColor) {
        if (outlineColor === backgroundColor) {
            outlineColor = getContrastColor(backgroundColor);
        }
        const backgroundToOutlineContrastAnalysis = checkContrast(backgroundColor, outlineColor);
        if (!backgroundToOutlineContrastAnalysis.sufficientContrast) {
            outlineColor = backgroundToOutlineContrastAnalysis.replacementColor;
        }
    }
    
    let textColor = primaryColor;
    
    console.log('🎨 BEFORE CONTRAST LOGIC - textColor:', textColor);
    
    // Apply simple contrast logic like the original (regardless of active/animation type)
    if (textColor === backgroundColor && backgroundColor === primaryColor) {
        textColor = getContrastColor(backgroundColor);
        console.log('🎨 SIMPLE CONTRAST 1: backgroundColor === primaryColor, textColor set to:', textColor);
        if (outlineColor && outlineColor === textColor) {
            textColor = getContrastColor(outlineColor);
            console.log('🎨 SIMPLE CONTRAST 2: outlineColor === textColor, textColor set to:', textColor);
        }
    }
    if (textColor === outlineColor) {
        textColor = getContrastColor(outlineColor);
        console.log('🎨 SIMPLE CONTRAST 3: textColor === outlineColor, textColor set to:', textColor);
    }
    
    console.log('🎨 AFTER SIMPLE CONTRAST - textColor:', textColor);
    
    if (!active) {
        console.log('🎨 NON-ACTIVE WORD: Returning simple contrast result');
        return { outlineColor, textColor };
    } else {
        console.log('🎨 ACTIVE WORD LOGIC');
        if (isTextAnimationWithBox(textAnimation)) {
            console.log('🎨 ACTIVE BOX ANIMATION');
            // Apply contrast logic for box animations
            if (textColor === backgroundColor && backgroundColor === primaryColor) {
                textColor = getContrastColor(backgroundColor);
            }
            if (textColor === outlineColor) {
                textColor = getContrastColor(outlineColor);
            }
            
            if (!outlineColor) {
                const highlightToTextColorContrastAnalysis = checkContrast(currentSpokenWordColor, textColor);
                if (!highlightToTextColorContrastAnalysis.sufficientContrast) {
                    textColor = highlightToTextColorContrastAnalysis.replacementColor;
                }
            }
            console.log('🎨 ACTIVE BOX FINAL:', { outlineColor, textColor });
            return { outlineColor, textColor };
        }
        if (isTextAnimationWithTextHighlightColor(textAnimation)) {
            console.log('🎨 ACTIVE HIGHLIGHT ANIMATION:', textAnimation, 'isKaraoke:', isKaraokeAnimation(textAnimation));
            // For highlight animations (including karaoke), active words get highlight color
            textColor = currentSpokenWordColor;
            console.log('🎨 ACTIVE WORD: textColor set to currentSpokenWordColor:', textColor);
            
            if (outlineColor) {
                const outlineToTextColorContrastAnalysis = checkContrast(outlineColor, textColor);
                console.log('🎨 OUTLINE CONTRAST CHECK:', { 
                    outlineColor, 
                    textColor, 
                    sufficientContrast: outlineToTextColorContrastAnalysis.sufficientContrast 
                });
                if (!outlineToTextColorContrastAnalysis.sufficientContrast) {
                    outlineColor = currentSpokenWordColor;
                    textColor = getContrastColor(outlineColor);
                    console.log('🎨 OUTLINE CONTRAST APPLIED: adjusted textColor to:', textColor, 'outlineColor to:', outlineColor);
                }
            }
        } else {
            console.log('🎨 ACTIVE NO HIGHLIGHT ANIMATION for:', textAnimation);
            // Apply standard contrast logic for non-highlight animations
            if (textColor === backgroundColor && backgroundColor === primaryColor) {
                textColor = getContrastColor(backgroundColor);
            }
            if (textColor === outlineColor) {
                textColor = getContrastColor(outlineColor);
            }
        }
        
        console.log('🎨 ACTIVE FINAL RESULT:', { textColor, outlineColor });
        return { outlineColor, textColor };
    }
};

/**
 * Token rendering information calculation
 */
const getTokenRenderInfo = (
    token: TextToken,
    index: number,
    timeInMs: number,
    pageStartMs: number,
    textAnimation: TextAnimationType,
    outline: OutlineType,
    background: BackgroundType,
    currentSpokenWordColor: string,
    primaryColor: string
) => {
    const startRelativeToSequence = token.fromMs - pageStartMs;
    const endRelativeToSequence = token.toMs - pageStartMs;
    const active = startRelativeToSequence <= timeInMs && endRelativeToSequence > timeInMs;
    const hasBeenSpoken = endRelativeToSequence <= timeInMs;

    const { outlineColor, textColor } = getOutlineAndTextColorForStyle(
        outline,
        textAnimation,
        active,
        background,
        currentSpokenWordColor,
        primaryColor,
        hasBeenSpoken
    );

    return {
        active,
        outlineColor,
        textColor,
        startRelativeToSequence,
        endRelativeToSequence
    };
};

/**
 * Text processing utilities
 */
const processText = (text: string, captionCase: CaptionCase, hidePunctuation: boolean): string => {
    let processed = text;
    
    if (captionCase === 'uppercase') {
        processed = processed.toUpperCase();
    }
    
    if (hidePunctuation) {
        processed = processed.replace(/[.,!?;:]/g, '');
    }
    
    return processed;
};

/**
 * Outline rendering for different effect types
 */
const renderOutline = ({
    outline,
    layerType,
    token,
    index,
    positions,
    verticalPosition,
    fontSize,
    fontFamily,
    outlineColor,
    opacity,
    combinedTransform,
    processText: processTextFn
}: OutlineRenderProps) => {
    if (outline === 'none') {
        return null;
    }

    const text = processTextFn(token.text);
    const x = positions[index].x + (positions[index].width / 2);

    if (outline === 'glow' && layerType === 'glow') {
        if (opacity === 0) return null;
        return [
            <text
                key={`glow1-${token.fromMs}-${index}`}
                x={x} y={verticalPosition}
                dominantBaseline="middle" textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="rgba(255,255,255,0.3)"
                transform={`${combinedTransform} translate(3,3)`}
                filter="blur(10px)" opacity={opacity * 0.3}
            >
                {text}
            </text>,
            <text
                key={`glow2-${token.fromMs}-${index}`}
                x={x} y={verticalPosition}
                dominantBaseline="middle" textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="rgba(255,255,255,0.4)"
                transform={`${combinedTransform} translate(1.5,1.5)`}
                filter="blur(5px)" opacity={opacity * 0.4}
            >
                {text}
            </text>,
            <text
                key={`glow3-${token.fromMs}-${index}`}
                x={x} y={verticalPosition}
                dominantBaseline="middle" textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="rgba(255,255,255,0.5)"
                transform={`${combinedTransform} translate(1,1)`}
                filter="blur(3px)" opacity={opacity * 0.5}
            >
                {text}
            </text>
        ];
    }

    if (outline === 'shadow' && layerType === 'other') {
        if (opacity === 0) return null;
        return [
            <text
                key={`shadow1-${token.fromMs}-${index}`}
                x={x} y={verticalPosition}
                dominantBaseline="middle" textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="black"
                transform={`${combinedTransform} translate(1.5,1.5)`}
                filter="blur(3px)" opacity={opacity * 0.15}
                stroke="black" strokeWidth={fontSize * 0.015}
            >
                {text}
            </text>,
            <text
                key={`shadow2-${token.fromMs}-${index}`}
                x={x} y={verticalPosition}
                dominantBaseline="middle" textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="black"
                transform={`${combinedTransform} translate(0.5,0.5)`}
                filter="blur(1.5px)" opacity={opacity * 0.2}
                stroke="black" strokeWidth={fontSize * 0.004}
            >
                {text}
            </text>,
            <text
                key={`shadow3-${token.fromMs}-${index}`}
                x={x} y={verticalPosition}
                dominantBaseline="middle" textAnchor="middle"
                style={{ fontSize, fontFamily }}
                fill="black"
                transform={`${combinedTransform} translate(0.3,0.3)`}
                filter="blur(0.8px)" opacity={opacity * 0.25}
                stroke="black" strokeWidth={fontSize * 0.001}
            >
                {text}
            </text>
        ];
    }

    if (outline === '3d-shadow' && layerType === 'other') {
        if (opacity === 0 || !outlineColor) return null;

        return Array.from({ length: 4 }).map((_, i) => {
            const offset = (fontSize * 0.06 / 4) * (i + 1);
            const baseLayerOpacity = 0.5 + (i * 0.125);
            const finalLayerOpacity = baseLayerOpacity * opacity;

            return (
                <text
                    key={`shadow-layer-${i}-${token.fromMs}-${index}`}
                    x={x} y={verticalPosition + offset}
                    dominantBaseline="middle" textAnchor="middle"
                    style={{ fontSize, fontFamily }}
                    fill={outlineColor} stroke={outlineColor}
                    strokeWidth={fontSize * 0.06}
                    strokeLinejoin="round" strokeLinecap="round"
                    opacity={finalLayerOpacity} transform={combinedTransform}
                >
                    {text}
                </text>
            );
        });
    }

    if ((outline === 'stroke' || outline === '3d-stroke') && layerType === 'other') {
        if (opacity === 0 || !outlineColor) return null;

        if (outline === '3d-stroke') {
            return Array.from({ length: 4 }).map((_, i) => {
                const offset = (fontSize * 0.06 / 4) * (i + 1);
                return (
                    <text
                        key={`3d-stroke-layer-${i}-${token.fromMs}-${index}`}
                        x={x} y={verticalPosition + offset}
                        dominantBaseline="middle" textAnchor="middle"
                        style={{ fontSize, fontFamily }}
                        fill="none" stroke={outlineColor}
                        strokeWidth={fontSize * 0.18}
                        strokeLinejoin="round" strokeLinecap="round"
                        opacity={opacity} transform={combinedTransform}
                    >
                        {text}
                    </text>
                );
            });
        }

        return (
            <text
                key={`stroke-${token.fromMs}-${index}`}
                x={x} y={verticalPosition}
                dominantBaseline="middle" textAnchor="middle"
                style={{ fontSize, fontFamily }}
                stroke={outlineColor} strokeWidth={fontSize * 0.12}
                strokeLinejoin="round" strokeLinecap="round"
                fill="none" opacity={opacity} transform={combinedTransform}
            >
                {text}
            </text>
        );
    }

    return null;
};

/**
 * Token rendering with sophisticated animations
 */
const renderTokens = (
    layerType: LayerType,
    tokens: TextToken[],
    positions: Array<{ x: number; width: number }>,
    baseVerticalPosition: number,
    fontSize: number,
    fontFamily: string,
    processTextFn: (text: string) => string,
    textAnimation: TextAnimationType = 'none',
    outline: OutlineType = 'none',
    background: BackgroundType = 'none',
    currentSpokenWordColor: string = '#000000',
    primaryColor: string = '#000000',
    timeInMs: number = 0,
    pageStartMs: number = 0,
    fps: number = 30
) => {
    return tokens.map((token, index) => {
        const renderInfo = getTokenRenderInfo(
            token,
            index,
            timeInMs,
            pageStartMs,
            textAnimation,
            outline,
            background,
            currentSpokenWordColor,
            primaryColor
        );

        const { active, outlineColor, textColor, startRelativeToSequence, endRelativeToSequence } = renderInfo;
        
        // Calculate word state for animation logic
        const hasBeenSpoken = endRelativeToSequence <= timeInMs;
        const notYetSpoken = startRelativeToSequence > timeInMs;

        const wordWidth = positions[index].width;
        const wordX = positions[index].x;
        const wordCenterX = wordX + (wordWidth / 2);
        const verticalPosition = baseVerticalPosition + (token.verticalOffset !== undefined ? token.verticalOffset : 0);

        let scale = 1;
        let revealTransform = '';
        let animatedOpacity = 1;

        if (active) {
            const timeSinceTokenActiveMs = timeInMs - startRelativeToSequence;
            const framesSinceTokenActive = Math.max(0, Math.floor(timeSinceTokenActiveMs / (1000 / fps)));

            if (textAnimation === 'pop') {
                const popDurationFrames = 15;
                const progress = spring({
                    frame: framesSinceTokenActive,
                    fps: fps,
                    config: { damping: 15, stiffness: 180, mass: 0.7 },
                    durationInFrames: popDurationFrames,
                });
                scale = interpolate(progress, [0, 0.5, 1], [1, 1.25, 1]);
            } else if (textAnimation === 'scale' ||
                       textAnimation === 'reveal_scale' ||
                       textAnimation === 'karaoke_scale') {
                scale = spring({
                    frame: framesSinceTokenActive,
                    fps: fps,
                    from: 1,
                    to: 1.1,
                    durationInFrames: 20,
                    config: { damping: 20, stiffness: 100, mass: 0.6 }
                });
            }

            if (textAnimation === 'reveal' || textAnimation === 'reveal_up' || textAnimation === 'reveal_scale' || textAnimation === 'reveal_box') {
                const revealDurationFrames = 12;
                if (framesSinceTokenActive < revealDurationFrames) {
                    const slideAmount = spring({
                        frame: framesSinceTokenActive,
                        fps: fps,
                        durationInFrames: revealDurationFrames,
                        config: { damping: 15, stiffness: 200, mass: 0.6 }
                    });
                    if (textAnimation === 'reveal_up') {
                        const slideY = interpolate(slideAmount, [0, 1], [fontSize * 0.3, 0]);
                        revealTransform = `translate(0, ${slideY})`;
                    } else {
                        const slideX = interpolate(slideAmount, [0, 1], [-fontSize * 0.3, 0]);
                        revealTransform = `translate(${slideX}, 0)`;
                    }
                }
            }

            if (textAnimation === 'slide_left' || textAnimation === 'slide_up') {
                const slideDurationFrames = 15;
                const slideProgress = spring({
                    frame: framesSinceTokenActive,
                    fps: fps,
                    durationInFrames: slideDurationFrames,
                    config: { damping: 18, stiffness: 150, mass: 0.8 }
                });
                
                if (textAnimation === 'slide_left') {
                    const slideX = interpolate(slideProgress, [0, 1], [-fontSize * 0.5, 0]);
                    revealTransform = `translate(${slideX}, 0)`;
                } else if (textAnimation === 'slide_up') {
                    const slideY = interpolate(slideProgress, [0, 1], [fontSize * 0.5, 0]);
                    revealTransform = `translate(0, ${slideY})`;
                }
            }

            if (textAnimation === 'karaoke' || textAnimation === 'karaoke_scale' || textAnimation === 'karaoke_box') {
                const karaokeOpacity = Math.max(0, Math.min(1, (timeInMs - startRelativeToSequence) / 200));
                animatedOpacity = karaokeOpacity;
            }
        }

        const scaleTransform = scale !== 1 ? `translate(${wordCenterX}, ${verticalPosition}) scale(${scale}) translate(${-wordCenterX}, ${-verticalPosition})` : '';
        const combinedTransform = [scaleTransform, revealTransform].filter(Boolean).join(' ');

        // Calculate opacity based on animation type and word timing
        let finalOpacity = 1;
        const isKaraokeStyle = textAnimation === 'karaoke' || textAnimation === 'karaoke_scale' || textAnimation === 'karaoke_box';
        
        if (isKaraokeStyle) {
            // Karaoke style: Calculate base karaoke opacity like the original
            let karaokeBaseOpacity;
            if (active) {
                karaokeBaseOpacity = animatedOpacity; // Progressive opacity for currently active word
            } else if (hasBeenSpoken) {
                karaokeBaseOpacity = 1.0; // Already spoken - full opacity
            } else {
                karaokeBaseOpacity = 0.3; // Not yet spoken - reduced opacity  
            }
            
            // Apply karaoke opacity to all layers (outline and main)
            finalOpacity = karaokeBaseOpacity;
        } else {
            // Non-karaoke animations: only show spoken and currently speaking words
            if (active) {
                finalOpacity = animatedOpacity;
            } else if (hasBeenSpoken) {
                finalOpacity = 1; // Already spoken words are fully visible
            } else if (notYetSpoken) {
                finalOpacity = 0; // Future words are hidden
            }
        }

        if (layerType !== 'main' && outline !== 'none') {
            return renderOutline({
                outline,
                layerType,
                token,
                index,
                positions,
                verticalPosition,
                fontSize,
                fontFamily,
                outlineColor,
                opacity: finalOpacity,
                combinedTransform,
                processText: processTextFn
            });
        }

        if (layerType === 'main') {
            return (
                <text
                    key={`main-${token.fromMs}-${index}`}
                    x={wordCenterX}
                    y={verticalPosition}
                    dominantBaseline="middle"
                    textAnchor="middle"
                    style={{ fontSize, fontFamily }}
                    fill={textColor}
                    opacity={finalOpacity}
                    transform={combinedTransform}
                >
                    {processTextFn(token.text)}
                </text>
            );
        }

        return null;
    });
};

/**
 * Main Advanced Text Engine Component
 */
export const AdvancedTextEngine: React.FC<AdvancedTextEngineProps> = ({
    page,
    config,
    videoConfig,
    currentFrame,
    position = 'center',
    availableWidth,
    availableHeight
}) => {
    const frame = useCurrentFrame();
    const actualFrame = currentFrame !== undefined ? currentFrame : frame;
    
    // Safety check: Ensure tokens are present
    if (!page.tokens || page.tokens.length === 0) {
        return null; // or return a simple text fallback
    }
    
    // Calculate current time in milliseconds
    const timeInMs = (actualFrame / videoConfig.fps) * 1000;
    
    // Text processing function
    const processTextFn = (text: string) => processText(text, config.captionCase, config.hidePunctuation);
    
    // Font family resolution using enhanced clipCreator integration
    const fontFamily = resolveEnhancedFontFamily(config.fontFamily);
    
    // Responsive font scaling - calculate optimal font size based on available space
    let effectiveFontSize = config.maxFontSize;
    
    if (availableWidth && typeof document !== 'undefined') {
        // Create temporary canvas for text measurement
        const tempCanvas = document.createElement('canvas');
        const tempContext = tempCanvas.getContext('2d');
        
        if (tempContext) {
            // Test current font size
            tempContext.font = `${config.maxFontSize}px ${fontFamily}`;
            
            // Calculate total text width with current font size
            const words = page.tokens?.map(t => processText(t.text, config.captionCase, config.hidePunctuation)) || [];
            const spaceWidth = tempContext.measureText(' ').width * 1.2;
            const totalTextWidth = words.reduce((acc, word, i) => {
                const wordWidth = tempContext.measureText(word).width;
                return acc + wordWidth + (i > 0 ? spaceWidth : 0);
            }, 0);
            
            // Calculate container padding at current font size
            const containerPaddingX = config.maxFontSize * config.containerPadding.x * 2;
            const containerMargin = config.maxFontSize * 0.1;
            const requiredWidth = totalTextWidth + containerPaddingX + containerMargin;
            
            // Scale down font if text exceeds available width
            if (requiredWidth > availableWidth) {
                const scaleFactor = availableWidth / requiredWidth;
                effectiveFontSize = Math.max(config.maxFontSize * scaleFactor, 12); // Minimum 12px font
                console.log(`[AdvancedTextEngine] Responsive sizing: ${config.maxFontSize}px → ${effectiveFontSize.toFixed(1)}px (${scaleFactor.toFixed(2)}x scale)`);
            }
        }
    }
    
    // Calculate actual padding values based on effective font size
    const actualContainerPadding = {
        x: effectiveFontSize * config.containerPadding.x,
        y: effectiveFontSize * config.containerPadding.y
    };
    
    const actualBoxPadding = {
        x: effectiveFontSize * config.boxPadding.x,
        y: effectiveFontSize * config.boxPadding.y
    };
    
    const actualBorderRadius = effectiveFontSize * config.borderRadius;
    
    // Cache canvas for font measurements
    let measurementCanvas: HTMLCanvasElement | null = null;
    let measurementContext: CanvasRenderingContext2D | null = null;

    // Calculate maximum scale factor for current animation to ensure container accommodates scaled text
    const scaleFactor = getMaxScaleFactor(config.textAnimation);

    // Canvas-based font measurement for precise positioning
    const getWordPositions = (finalContainerWidth: number): Array<{ x: number; width: number }> => {
        if (typeof document === 'undefined') return [];
        
        if (!measurementCanvas) {
            measurementCanvas = document.createElement('canvas');
            measurementContext = measurementCanvas.getContext('2d');
        }
        const context = measurementContext;
        if (!context) return [];

        context.font = `${effectiveFontSize}px ${fontFamily}`;
        
        const words = page.tokens?.map(t => processTextFn(t.text)) || [];
        const baseSpaceWidth = context.measureText(' ').width;
        const spaceWidth = baseSpaceWidth * 1.2;
        
        const totalTextWidth = words.reduce((acc, word, i) => {
            const wordWidth = context.measureText(word).width;
            return acc + wordWidth + (i > 0 ? spaceWidth : 0);
        }, 0);
        
        // Use the provided final container width for perfect centering
        const startX = (finalContainerWidth - totalTextWidth) / 2;
        
        let currentX = startX;
        return page.tokens?.map((token, index) => {
            if (index > 0) {
                currentX += spaceWidth;
            }

            const text = processTextFn(token.text);
            const width = context.measureText(text).width;
            const position = { x: currentX, width: width };
            currentX += width;

            return position;
        }) || [];
    };

    // First calculate the final container width
    if (typeof document !== 'undefined') {
        if (!measurementCanvas) {
            measurementCanvas = document.createElement('canvas');
            measurementContext = measurementCanvas.getContext('2d');
        }
        const context = measurementContext;
        if (context) {
            context.font = `${effectiveFontSize}px ${fontFamily}`;
            const words = page.tokens?.map(t => processTextFn(t.text)) || [];
            const baseSpaceWidth = context.measureText(' ').width;
            const spaceWidth = baseSpaceWidth * 1.2;
            
            const totalTextWidth = words.reduce((acc, word, i) => {
                const wordWidth = context.measureText(word).width;
                return acc + wordWidth + (i > 0 ? spaceWidth : 0);
            }, 0);
            
            const baseContainerWidth = totalTextWidth + (actualContainerPadding.x * 2) + (effectiveFontSize * 0.1);
            var containerWidth = baseContainerWidth * scaleFactor;
        }
    }
    
    // Fallback if canvas measurement fails
    if (!containerWidth) {
        containerWidth = 800 * scaleFactor; // Reasonable fallback
    }

    const positions = getWordPositions(containerWidth);
    if (positions.length === 0) return null;
    
    const svgHeight = (() => {
        let baseHeight = effectiveFontSize * 1.15;
        
        if (config.outline === '3d-stroke') {
            baseHeight = effectiveFontSize * 1.25;
        } else if (config.outline === '3d-shadow') {
            baseHeight = effectiveFontSize * 1.2;
        } else if (config.outline === 'shadow' || config.outline === 'stroke') {
            baseHeight = effectiveFontSize * 1.15;
        } else if (config.outline === 'glow') {
            baseHeight = effectiveFontSize * 1.15;
        } else {
            baseHeight = effectiveFontSize * 1.1;
        }
        
        if (config.background !== 'none') {
            baseHeight += (actualContainerPadding.y * 2);
        }
        
        // Apply scale buffer to height as well to accommodate scaled text
        return baseHeight * scaleFactor;
    })();
    
    const centerY = (() => {
        let center = svgHeight / 2;
        
        if (config.outline === '3d-stroke') {
            center = svgHeight / 2 - effectiveFontSize * 0.02;
        } else if (config.outline === '3d-shadow') {
            center = svgHeight / 2 - effectiveFontSize * 0.02;
        }
        
        return center;
    })();

    // Calculate which words should be visible based on animation type
    const isKaraokeStyle = config.textAnimation === 'karaoke' || config.textAnimation === 'karaoke_scale' || config.textAnimation === 'karaoke_box';
    
    
    // FIXED: Background visibility logic
    // - Karaoke: Always show background (all words are part of the experience)  
    // - Non-karaoke: Only show background if there are currently visible words (active + spoken)
    const shouldShowBackground = (() => {
        if (config.background === 'none') return false;
        
        if (isKaraokeStyle) {
            // FIXED: For karaoke with translucent backgrounds, only show background for active words
            // This matches user expectation that only currently speaking word gets background
            if (config.background === 'translucent') {
                const hasActiveWord = page.tokens.some((token, index) => {
                    const renderInfo = getTokenRenderInfo(
                        token, index, timeInMs, page.startMs,
                        config.textAnimation, config.outline, config.background,
                        config.currentSpokenWordColor, config.primaryColor
                    );
                    return renderInfo.active;
                });
                return hasActiveWord;
            } else {
                // Other backgrounds (colored, white) show for entire phrase in karaoke
                return true;
            }
        } else {
            // Non-karaoke: only show background if there are words that should be visible
            const hasVisibleWords = page.tokens.some((token, index) => {
                const renderInfo = getTokenRenderInfo(
                    token, index, timeInMs, page.startMs,
                    config.textAnimation, config.outline, config.background,
                    config.currentSpokenWordColor, config.primaryColor
                );
                // Show for active word or spoken words (past words)
                const hasBeenSpoken = renderInfo.endRelativeToSequence <= timeInMs;
                return renderInfo.active || hasBeenSpoken;
            });
            return hasVisibleWords;
        }
    })();
    
    
    return (
        <FontLoader fonts={[config.fontFamily]} initializeClipCreatorFonts={true}>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%'
            }}>
            <div style={{
                display: 'inline-block',
                backgroundColor: shouldShowBackground ? backgroundToCSS(config.background, config.primaryColor) : 'transparent',
                borderRadius: actualBorderRadius,
                paddingLeft: shouldShowBackground ? actualContainerPadding.x : 0,
                paddingRight: shouldShowBackground ? actualContainerPadding.x : 0,
                paddingTop: shouldShowBackground ? actualContainerPadding.y : 0,
                paddingBottom: shouldShowBackground ? actualContainerPadding.y : 0,
                overflow: 'visible',
                textAlign: 'center'
            }}>
                <svg 
                    style={{
                        width: 'auto',
                        height: 'auto',
                        display: 'block',
                    }}
                    width={containerWidth}
                    height={svgHeight}
                    viewBox={`0 0 ${containerWidth} ${svgHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                >
                    {/* Background boxes for boxed animations */}
                    {isTextAnimationWithBox(config.textAnimation) && 
                        page.tokens.map((token, index) => {
                            const renderInfo = getTokenRenderInfo(
                                token, index, timeInMs, page.startMs,
                                config.textAnimation, config.outline, config.background,
                                config.currentSpokenWordColor, config.primaryColor
                            );
                            
                            // CRITICAL FIX: Boxes should ONLY render for currently active words
                            // This matches the original ClassicCaptionPage logic: if (!active) return null;
                            if (!renderInfo.active) return null;

                            const wordWidth = positions[index].width;
                            const outlinePadding = (() => {
                                if (config.outline === '3d-shadow') return effectiveFontSize * 0.15;
                                if (config.outline === '3d-stroke') return effectiveFontSize * 0.18;
                                if (config.outline === 'stroke') return effectiveFontSize * 0.12;
                                if (config.outline === 'shadow' || config.outline === 'glow') return effectiveFontSize * 0.08;
                                return 0;
                            })();

                            let boxHeight, boxY;
                            if (config.outline === '3d-shadow') {
                                boxHeight = effectiveFontSize * 1.15 + (actualBoxPadding.y * 2);
                                boxY = centerY - (effectiveFontSize * 0.55) - actualBoxPadding.y;
                            } else if (config.outline === '3d-stroke') {
                                boxHeight = effectiveFontSize * 1.2 + (actualBoxPadding.y * 2);
                                boxY = centerY - (effectiveFontSize * 0.58) - actualBoxPadding.y;
                            } else if (config.outline === 'stroke') {
                                boxHeight = effectiveFontSize * 1.1 + (actualBoxPadding.y * 2);
                                boxY = centerY - (effectiveFontSize * 0.55) - actualBoxPadding.y;
                            } else {
                                boxHeight = effectiveFontSize * 1.10 + (actualBoxPadding.y * 2);
                                boxY = centerY - (effectiveFontSize * 0.55) - actualBoxPadding.y;
                            }

                            const totalBoxPadding = { x: actualBoxPadding.x + outlinePadding, y: actualBoxPadding.y };
                            
                            // Box animation
                            const framesSinceTokenActive = Math.max(0, Math.floor((timeInMs - renderInfo.startRelativeToSequence) / (1000 / videoConfig.fps)));
                            const scaleAmount = spring({
                                frame: framesSinceTokenActive,
                                fps: videoConfig.fps,
                                from: 0.8,
                                to: 1,
                                durationInFrames: 10,
                                config: { damping: 12, stiffness: 180, mass: 0.5 }
                            });
                            
                            const boxCenterX = positions[index].x + wordWidth / 2;
                            let boxTransform = `translate(${boxCenterX}, ${centerY}) scale(${scaleAmount}) translate(${-boxCenterX}, ${-centerY})`;

                            if (config.textAnimation === 'reveal_box') {
                                const revealDuration = 12;
                                const slideAmountReveal = spring({
                                    frame: framesSinceTokenActive,
                                    fps: videoConfig.fps,
                                    durationInFrames: revealDuration,
                                    config: { damping: 15, stiffness: 200, mass: 0.6 }
                                });
                                const slideX = interpolate(slideAmountReveal, [0, 1], [-effectiveFontSize * 0.3, 0]);
                                boxTransform += ` translate(${slideX}, 0)`;
                            }

                            return (
                                <rect
                                    key={`box-${token.fromMs}-${index}`}
                                    x={positions[index].x - totalBoxPadding.x}
                                    y={boxY}
                                    width={wordWidth + (totalBoxPadding.x * 2)}
                                    height={boxHeight}
                                    fill={config.currentSpokenWordColor}
                                    rx={actualBorderRadius}
                                    ry={actualBorderRadius}
                                    transform={boxTransform}
                                    opacity={1}
                                />
                            );
                        })
                    }

                    {/* Outline layers */}
                    {config.outline === 'shadow' && 
                        renderTokens('other', page.tokens, positions, centerY, effectiveFontSize, fontFamily, 
                            processTextFn, config.textAnimation, config.outline, config.background, 
                            config.currentSpokenWordColor, config.primaryColor, timeInMs, page.startMs, videoConfig.fps)
                    }
                    
                    {(config.outline === '3d-stroke' || config.outline === '3d-shadow') && 
                        renderTokens('other', page.tokens, positions, centerY, effectiveFontSize, fontFamily, 
                            processTextFn, config.textAnimation, config.outline, config.background, 
                            config.currentSpokenWordColor, config.primaryColor, timeInMs, page.startMs, videoConfig.fps)
                    }
                    
                    {config.outline === 'stroke' && 
                        renderTokens('other', page.tokens, positions, centerY, effectiveFontSize, fontFamily, 
                            processTextFn, config.textAnimation, config.outline, config.background, 
                            config.currentSpokenWordColor, config.primaryColor, timeInMs, page.startMs, videoConfig.fps)
                    }
                    
                    {config.outline === 'glow' && 
                        renderTokens('glow', page.tokens, positions, centerY, effectiveFontSize, fontFamily, 
                            processTextFn, config.textAnimation, config.outline, config.background, 
                            config.currentSpokenWordColor, config.primaryColor, timeInMs, page.startMs, videoConfig.fps)
                    }

                    {/* Main text layer - always last */}
                    {renderTokens('main', page.tokens, positions, centerY, effectiveFontSize, fontFamily, 
                        processTextFn, config.textAnimation, config.outline, config.background, 
                        config.currentSpokenWordColor, config.primaryColor, timeInMs, page.startMs, videoConfig.fps)
                    }
                </svg>
            </div>
        </div>
        </FontLoader>
    );
};

export default AdvancedTextEngine;